import { z } from 'zod'
import { moneySchema, optionalText, pageQuerySchema, versionSchema } from './common.js'
import type { OrgUnitRef } from './org-units.js'

/**
 * Human resources (`hr` module, ADR-048): the employee file under the Omani Labour Law (RD 53/2023), its
 * contract versions, its effective-dated salary made of pay components, its documents with their expiries and
 * its status history. Payroll reads all of it; hr never reads payroll.
 */

/** Oman's ISO 3166-1 code: an employee of this nationality is Omani, any other is non-Omani. */
export const OMAN = 'OM'

export const NATIONALITY_CLASSES = ['OMANI', 'NON_OMANI'] as const
export type NationalityClass = (typeof NATIONALITY_CLASSES)[number]

export function nationalityClass(nationality: string): NationalityClass {
  return nationality.toUpperCase() === OMAN ? 'OMANI' : 'NON_OMANI'
}

/**
 * Nationalities offered first in pickers (the Omani workforce's most common, then the GCC); every other
 * ISO 3166-1 alpha-2 code is accepted too. Names come from `Intl.DisplayNames` in the reader's language.
 */
export const COMMON_NATIONALITIES = [
  'OM',
  'IN',
  'PK',
  'BD',
  'NP',
  'LK',
  'PH',
  'EG',
  'JO',
  'SY',
  'LB',
  'SD',
  'YE',
  'AE',
  'SA',
  'KW',
  'QA',
  'BH',
  'GB',
  'US',
] as const

/**
 * Every ISO 3166-1 alpha-2 code, for the nationality picker. Packed in one string to keep the bundle small.
 */
export const ISO_COUNTRY_CODES: readonly string[] = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW ' +
  'BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI ' +
  'FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN ' +
  'IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME ' +
  'MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF ' +
  'PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV ' +
  'SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE ' +
  'YT ZA ZM ZW'
).split(' ')

const isoCountries: ReadonlySet<string> = new Set(ISO_COUNTRY_CODES)

export const nationalitySchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine((v) => isoCountries.has(v), 'Choose a nationality')

/**
 * Labour Law starting values (RD 53/2023 as read from secondary sources, ASM-257): every one is a default the
 * contract form fills in and the officer may change.
 */
export const LABOUR_LAW_DEFAULTS = {
  probationMonths: 3,
  maxProbationMonths: 6,
  noticeDays: 30,
  weeklyHours: '45',
  annualLeaveDays: 30,
} as const

export const EMPLOYEE_STATUSES = ['PROBATION', 'ACTIVE', 'SUSPENDED', 'ENDED'] as const
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number]

export const GENDERS = ['MALE', 'FEMALE'] as const
export type Gender = (typeof GENDERS)[number]

export const MARITAL_STATUSES = ['SINGLE', 'MARRIED', 'DIVORCED', 'WIDOWED'] as const
export type MaritalStatus = (typeof MARITAL_STATUSES)[number]

/** How the employee is paid: by bank transfer through the Wage Protection System, or outside it. */
export const EMPLOYEE_PAY_METHODS = ['BANK', 'CASH', 'CHEQUE'] as const
export type EmployeePayMethod = (typeof EMPLOYEE_PAY_METHODS)[number]

export const CONTRACT_TYPES = ['UNLIMITED', 'FIXED_TERM', 'PART_TIME', 'TEMPORARY'] as const
export type ContractType = (typeof CONTRACT_TYPES)[number]

export const END_REASONS = [
  'RESIGNATION',
  'TERMINATION',
  'END_OF_CONTRACT',
  'FAILED_PROBATION',
  'RETIREMENT',
  'DEATH',
  'OTHER',
] as const
export type EndReason = (typeof END_REASONS)[number]

export const SALARY_CHANGE_REASONS = ['HIRE', 'INCREMENT', 'PROMOTION', 'ADJUSTMENT', 'CORRECTION'] as const
export type SalaryChangeReason = (typeof SALARY_CHANGE_REASONS)[number]

export const EMPLOYEE_DOCUMENT_TYPES = [
  'CIVIL_ID',
  'RESIDENT_CARD',
  'PASSPORT',
  'VISA',
  'WORK_PERMIT',
  'DRIVING_LICENCE',
  'HEALTH_CARD',
  'CONTRACT',
  'CERTIFICATE',
  'OTHER',
] as const
export type EmployeeDocumentType = (typeof EMPLOYEE_DOCUMENT_TYPES)[number]

export const EMPLOYEE_DOCUMENT_STATUSES = ['ACTIVE', 'CANCELLED'] as const
export type EmployeeDocumentStatus = (typeof EMPLOYEE_DOCUMENT_STATUSES)[number]

/** Expiry buckets against today's Muscat date: expired, within 7, 30 and 90 days. */
export const EXPIRY_BUCKETS = ['EXPIRED', 'D7', 'D30', 'D90'] as const
export type ExpiryBucket = (typeof EXPIRY_BUCKETS)[number]

/** The bucket of a document expiring in `days` days (negative: already expired), null beyond 90 days. */
export function expiryBucket(days: number | null): ExpiryBucket | null {
  if (days === null) return null
  if (days < 0) return 'EXPIRED'
  if (days <= 7) return 'D7'
  if (days <= 30) return 'D30'
  if (days <= 90) return 'D90'
  return null
}

const dateSchema = z.iso.date()
const optionalDate = dateSchema.nullish().transform((v) => v ?? null)
/** Hours, up to 2 decimals ("45", "22.5"). */
const hoursSchema = z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, 'Invalid hours')

/** The display name: first, middle and last name in English. */
export function employeeName(e: { firstName: string; middleName: string | null; lastName: string }): string {
  return [e.firstName, e.middleName, e.lastName].filter(Boolean).join(' ')
}

/** IBANs and account numbers are stored without spaces, upper case. */
const accountText = (max: number) =>
  z
    .string()
    .trim()
    .max(max + 10)
    .nullish()
    .transform((v) => (v ? v.replace(/\s+/g, '').toUpperCase() : null))
    .refine((v) => v === null || v.length <= max, `At most ${max} characters`)

// ── employee ──────────────────────────────────────────────────────────────────

const personalFields = {
  firstName: z.string().trim().min(1).max(100),
  middleName: optionalText(100),
  lastName: z.string().trim().min(1).max(100),
  /** The full name in Arabic, as on the civil ID or resident card. */
  nameAr: optionalText(200),
  gender: z.enum(GENDERS),
  birthDate: optionalDate,
  maritalStatus: z
    .enum(MARITAL_STATUSES)
    .nullish()
    .transform((v) => v ?? null),
  nationality: nationalitySchema,
  /** Omani civil ID or the resident card's civil number: the WPS file identifies the worker by it. */
  civilNumber: optionalText(30),
  passportNumber: optionalText(30),
  /** Social Protection Fund insurance number (Omani employees). */
  spfNumber: optionalText(30),
  /** Left out of every social contribution rule (e.g. a pensioner re-employed): a payroll fact HR records. */
  spfExempt: z.boolean().default(false),
  email: z
    .email()
    .nullish()
    .transform((v) => v ?? null),
  phone: optionalText(30),
  address: optionalText(500),
  emergencyContactName: optionalText(200),
  emergencyContactPhone: optionalText(30),
}

const jobFields = {
  /**
   * The employee's system login (ADR-049): links what the user earns (sales commission, technician
   * incentives) to the employee's payroll. One employee per user.
   */
  userId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  orgUnitId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  jobTitle: z.string().trim().min(1).max(200),
  jobTitleAr: optionalText(200),
}

const bankFields = {
  payMethod: z.enum(EMPLOYEE_PAY_METHODS).default('BANK'),
  /** The employee's bank as the WPS file names it (its BIC / short code, e.g. BMUSOMRX). */
  bankCode: accountText(20),
  /** IBAN (OM + 21 characters) or the bank account number. */
  iban: accountText(34),
}

export const employmentContractSchema = z
  .object({
    type: z.enum(CONTRACT_TYPES),
    startDate: dateSchema,
    endDate: optionalDate,
    probationMonths: z.number().int().min(0).max(LABOUR_LAW_DEFAULTS.maxProbationMonths).default(3),
    noticeDays: z.number().int().min(0).max(365).default(LABOUR_LAW_DEFAULTS.noticeDays),
    weeklyHours: hoursSchema.default(LABOUR_LAW_DEFAULTS.weeklyHours),
    note: optionalText(500),
  })
  .superRefine((v, ctx) => {
    if (v.type === 'FIXED_TERM' && !v.endDate)
      ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'A fixed-term contract needs an end date' })
    if (v.endDate && v.endDate < v.startDate)
      ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'Ends before it starts' })
  })
export type EmploymentContractInput = z.input<typeof employmentContractSchema>

export const salaryLineInputSchema = z.object({
  componentId: z.uuid(),
  amount: moneySchema.refine((v) => Number(v) > 0, 'Must be more than zero'),
})

const salaryLines = z
  .array(salaryLineInputSchema)
  .min(1, 'Add the basic salary')
  .max(30)
  .superRefine((lines, ctx) => {
    const seen = new Set<string>()
    lines.forEach((l, i) => {
      if (seen.has(l.componentId))
        ctx.addIssue({ code: 'custom', path: [i, 'componentId'], message: 'Listed twice' })
      seen.add(l.componentId)
    })
  })

export const employeeHireSchema = z.object({
  ...personalFields,
  ...jobFields,
  ...bankFields,
  /** The first working day: the contract, the salary and the service for gratuity start on it. */
  hireDate: dateSchema,
  contract: z.object({
    type: z.enum(CONTRACT_TYPES),
    endDate: optionalDate,
    probationMonths: z.number().int().min(0).max(LABOUR_LAW_DEFAULTS.maxProbationMonths).default(3),
    noticeDays: z.number().int().min(0).max(365).default(LABOUR_LAW_DEFAULTS.noticeDays),
    weeklyHours: hoursSchema.default(LABOUR_LAW_DEFAULTS.weeklyHours),
    note: optionalText(500),
  }),
  salary: salaryLines,
  /** A retry with the same id returns the employee the first request created. */
  requestId: z.uuid(),
})
export type EmployeeHireInput = z.input<typeof employeeHireSchema>

export const employeeUpdateSchema = z.object({
  ...personalFields,
  ...jobFields,
  ...bankFields,
  version: versionSchema,
})
export type EmployeeUpdateInput = z.input<typeof employeeUpdateSchema>

export const employeeVersionSchema = z.object({ version: versionSchema })
export type EmployeeVersionInput = z.input<typeof employeeVersionSchema>

export const employeeSuspendSchema = z.object({
  reason: z.string().trim().min(1).max(500),
  version: versionSchema,
})
export type EmployeeSuspendInput = z.input<typeof employeeSuspendSchema>

export const employeeEndSchema = z.object({
  /** The last working day: payroll pays up to it and settles the end of service in its month. */
  endedOn: dateSchema,
  reason: z.enum(END_REASONS),
  note: optionalText(500),
  version: versionSchema,
})
export type EmployeeEndInput = z.input<typeof employeeEndSchema>

/** A new contract version: the current one is superseded, never edited. */
export const contractInputSchema = employmentContractSchema
export type ContractInput = z.input<typeof contractInputSchema>

/** A new salary from a date: the earlier sets stay as they were (payroll prorates across them). */
export const salaryChangeSchema = z.object({
  effectiveFrom: dateSchema,
  reason: z.enum(SALARY_CHANGE_REASONS).refine((r) => r !== 'HIRE', 'Choose a reason'),
  note: optionalText(500),
  lines: salaryLines,
})
export type SalaryChangeInput = z.input<typeof salaryChangeSchema>

export const EMPLOYEE_LIST_STATUSES = ['CURRENT', 'ALL', ...EMPLOYEE_STATUSES] as const
export type EmployeeListStatus = (typeof EMPLOYEE_LIST_STATUSES)[number]
export const EMPLOYEE_SORTS = ['number', 'name', 'hireDate', 'status'] as const

export const employeeListQuerySchema = pageQuerySchema.extend({
  /** CURRENT (default): everyone not ended. */
  status: z.enum(EMPLOYEE_LIST_STATUSES).default('CURRENT'),
  nationality: z.enum(NATIONALITY_CLASSES).optional(),
  orgUnitId: z.uuid().optional(),
})
export type EmployeeListQuery = z.input<typeof employeeListQuerySchema>

export interface EmployeeRef {
  id: string
  number: string
  name: string
  nameAr: string | null
}

export interface EmployeeListItem extends EmployeeRef {
  status: EmployeeStatus
  nationality: string
  nationalityClass: NationalityClass
  orgUnit: OrgUnitRef | null
  jobTitle: string
  jobTitleAr: string | null
  hireDate: string
  endedOn: string | null
  /** Masked to its last 4 characters without employees.manage. */
  civilNumber: string | null
  /** Active documents expired or expiring within 30 days. */
  documentsDue: number
  /** The current salary's earnings a month; null without employees.view_pay. */
  monthlyGross: string | null
}

export interface EmployeeContract {
  id: string
  versionNo: number
  type: ContractType
  startDate: string
  endDate: string | null
  probationMonths: number
  probationEnd: string | null
  noticeDays: number
  weeklyHours: string
  note: string | null
  current: boolean
  supersededAt: string | null
  createdAt: string
  createdBy: { id: string; name: string } | null
}

export interface EmployeeSalaryLine {
  componentId: string
  code: string
  name: string
  nameAr: string | null
  kind: PayComponentKind
  amount: string
}

export interface EmployeeSalarySet {
  id: string
  effectiveFrom: string
  reason: SalaryChangeReason
  note: string | null
  lines: EmployeeSalaryLine[]
  /** The earnings a month (the "gross" of the contract). */
  totalEarnings: string
  /** Recurring deductions a month. */
  totalDeductions: string
  /** True for the set in force today. */
  current: boolean
  createdAt: string
  createdBy: { id: string; name: string } | null
}

export interface Employee extends EmployeeRef {
  status: EmployeeStatus
  firstName: string
  middleName: string | null
  lastName: string
  gender: Gender
  birthDate: string | null
  maritalStatus: MaritalStatus | null
  nationality: string
  nationalityClass: NationalityClass
  /** Masked to its last 4 characters without employees.manage. */
  civilNumber: string | null
  passportNumber: string | null
  spfNumber: string | null
  spfExempt: boolean
  email: string | null
  phone: string | null
  address: string | null
  emergencyContactName: string | null
  emergencyContactPhone: string | null
  orgUnit: OrgUnitRef | null
  /** The employee's system login (ADR-049). */
  user: { id: string; name: string; email: string } | null
  jobTitle: string
  jobTitleAr: string | null
  hireDate: string
  /** While on probation: the contract's probation end. */
  probationEnd: string | null
  suspendedReason: string | null
  endedOn: string | null
  endReason: EndReason | null
  endNote: string | null
  payMethod: EmployeePayMethod
  bankCode: string | null
  /** Masked to its last 4 characters without employees.view_pay. */
  iban: string | null
  /** Completed service in years with 2 decimals, to today or the last working day. */
  serviceYears: string
  contract: EmployeeContract | null
  /** The salary in force today; null without employees.view_pay. */
  salary: EmployeeSalarySet | null
  version: number
  createdAt: string
  updatedAt: string
}

export interface EmployeeStatusEvent {
  id: string
  fromStatus: EmployeeStatus | null
  toStatus: EmployeeStatus
  reason: string | null
  effectiveOn: string
  at: string
  actor: { id: string; name: string } | null
}

// ── documents ─────────────────────────────────────────────────────────────────

export const employeeDocumentInputSchema = z
  .object({
    type: z.enum(EMPLOYEE_DOCUMENT_TYPES),
    number: optionalText(50),
    issuedOn: optionalDate,
    expiresOn: optionalDate,
    issuedBy: optionalText(200),
    note: optionalText(500),
  })
  .superRefine((v, ctx) => {
    if (v.issuedOn && v.expiresOn && v.expiresOn < v.issuedOn)
      ctx.addIssue({ code: 'custom', path: ['expiresOn'], message: 'Expires before it was issued' })
  })
export type EmployeeDocumentInput = z.input<typeof employeeDocumentInputSchema>

export const employeeDocumentUpdateSchema = z.object({
  document: employeeDocumentInputSchema,
  version: versionSchema,
})
export type EmployeeDocumentUpdate = z.input<typeof employeeDocumentUpdateSchema>

export interface EmployeeDocument {
  id: string
  type: EmployeeDocumentType
  number: string | null
  issuedOn: string | null
  expiresOn: string | null
  issuedBy: string | null
  note: string | null
  status: EmployeeDocumentStatus
  /** Days from today's Muscat date to the expiry (negative: expired); null without an expiry. */
  daysToExpiry: number | null
  expiryBucket: ExpiryBucket | null
  version: number
}

export const expiringDocumentsQuerySchema = pageQuerySchema.extend({
  /** Every document up to this bucket (D90 by default: everything expired or due within 90 days). */
  within: z.enum(EXPIRY_BUCKETS).default('D90'),
  type: z.enum(EMPLOYEE_DOCUMENT_TYPES).optional(),
  orgUnitId: z.uuid().optional(),
})
export type ExpiringDocumentsQuery = z.input<typeof expiringDocumentsQuerySchema>

export interface ExpiringDocument extends EmployeeDocument {
  employee: EmployeeRef & { orgUnit: OrgUnitRef | null; nationality: string }
}

// ── dashboard ─────────────────────────────────────────────────────────────────

export interface HrDashboard {
  /** Everyone not ended. */
  headcount: number
  byStatus: Record<EmployeeStatus, number>
  omani: number
  nonOmani: number
  /** Omani share of the headcount, as a percentage with 1 decimal ("42.9"). */
  omanisationPercent: string
  joinersThisMonth: number
  leaversThisMonth: number
  /** Active documents of current employees by expiry bucket. */
  expiring: Record<ExpiryBucket, number>
  /** Probation ending within 30 days. */
  probationEndingSoon: EmployeeRef[]
  /** Fixed-term contracts ending within 60 days. */
  contractsEndingSoon: (EmployeeRef & { endDate: string })[]
  byOrgUnit: { orgUnit: OrgUnitRef | null; count: number }[]
  byNationality: { nationality: string; count: number }[]
  /** The current salaries' earnings a month; null without employees.view_pay. */
  monthlyEarnings: string | null
}

// ── pay components ────────────────────────────────────────────────────────────

export const PAY_COMPONENT_KINDS = ['EARNING', 'DEDUCTION'] as const
export type PayComponentKind = (typeof PAY_COMPONENT_KINDS)[number]

/** FIXED: part of the salary, paid every month; VARIABLE: entered on a payroll run (bonus, advance recovery…). */
export const PAY_COMPONENT_TYPES = ['FIXED', 'VARIABLE'] as const
export type PayComponentType = (typeof PAY_COMPONENT_TYPES)[number]

/** The one component every salary needs: social contributions, overtime and gratuity are priced on it. */
export const BASIC_COMPONENT_CODE = 'BASIC'

const componentCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z][A-Z0-9_]{1,29}$/, 'Use 2–30 letters, digits or underscores, starting with a letter')

export const payComponentInputSchema = z.object({
  code: componentCode,
  name: z.string().trim().min(1).max(100),
  nameAr: optionalText(100),
  kind: z.enum(PAY_COMPONENT_KINDS),
  type: z.enum(PAY_COMPONENT_TYPES),
  /** Counts in the contribution wage the social protection rules apply to. */
  spfContributory: z.boolean().default(false),
  /** Counts in the wage end-of-service gratuity is priced on (by law the basic wage). */
  gratuityBase: z.boolean().default(false),
  /** Paid for the days worked in a month someone joins or leaves, and reduced by unpaid days. */
  prorated: z.boolean().default(true),
  /** The GL account its amounts post to; empty: salaries expense (earnings) or payroll deductions payable. */
  accountId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  sortOrder: z.number().int().min(0).max(9999).default(100),
})
export type PayComponentInput = z.input<typeof payComponentInputSchema>

export const payComponentUpdateSchema = z.object({
  component: payComponentInputSchema,
  version: versionSchema,
})
export type PayComponentUpdate = z.input<typeof payComponentUpdateSchema>

export interface PayComponent {
  id: string
  code: string
  name: string
  nameAr: string | null
  kind: PayComponentKind
  type: PayComponentType
  spfContributory: boolean
  gratuityBase: boolean
  prorated: boolean
  account: { id: string; code: string; name: string; nameAr: string | null } | null
  sortOrder: number
  /** BASIC: cannot be archived, renamed to another code or made variable. */
  system: boolean
  archived: boolean
  /** On a salary or a payroll run: its code and kind can no longer change. */
  inUse: boolean
  version: number
}

type StandardComponent = Omit<z.output<typeof payComponentInputSchema>, 'accountId'>

const component = (
  code: string,
  name: string,
  nameAr: string,
  kind: PayComponentKind,
  type: PayComponentType,
  flags: { spf?: boolean; gratuity?: boolean; prorated?: boolean },
  sortOrder: number,
): StandardComponent => ({
  code,
  name,
  nameAr,
  kind,
  type,
  spfContributory: flags.spf ?? false,
  gratuityBase: flags.gratuity ?? false,
  prorated: flags.prorated ?? true,
  sortOrder,
})

/**
 * The standard Omani salary structure loaded by "Load standard components" (idempotent by code). Which
 * allowances count in the contribution wage is ASM-258: every fixed allowance by default, editable.
 */
export const STANDARD_PAY_COMPONENTS: readonly StandardComponent[] = [
  component('BASIC', 'Basic salary', 'الراتب الأساسي', 'EARNING', 'FIXED', { spf: true, gratuity: true }, 10),
  component('HOUSING', 'Housing allowance', 'بدل السكن', 'EARNING', 'FIXED', { spf: true }, 20),
  component('TRANSPORT', 'Transport allowance', 'بدل النقل', 'EARNING', 'FIXED', { spf: true }, 30),
  component('PHONE', 'Phone allowance', 'بدل الهاتف', 'EARNING', 'FIXED', { spf: true }, 40),
  component('FOOD', 'Food allowance', 'بدل الطعام', 'EARNING', 'FIXED', { spf: true }, 50),
  component('OTHER_ALLOWANCE', 'Other allowance', 'بدلات أخرى', 'EARNING', 'FIXED', { spf: true }, 60),
  component('BONUS', 'Bonus', 'مكافأة', 'EARNING', 'VARIABLE', { prorated: false }, 110),
  component('COMMISSION', 'Commission', 'عمولة', 'EARNING', 'VARIABLE', { prorated: false }, 120),
  component(
    'ARREARS',
    'Salary arrears',
    'فروقات الراتب',
    'EARNING',
    'VARIABLE',
    { spf: true, prorated: false },
    130,
  ),
  component(
    'ADVANCE_RECOVERY',
    'Salary advance recovery',
    'استرداد سلفة الراتب',
    'DEDUCTION',
    'VARIABLE',
    { prorated: false },
    210,
  ),
  component(
    'LOAN_DEDUCTION',
    'Loan instalment',
    'قسط القرض',
    'DEDUCTION',
    'VARIABLE',
    { prorated: false },
    220,
  ),
  component(
    'PENALTY',
    'Disciplinary deduction',
    'جزاء تأديبي',
    'DEDUCTION',
    'VARIABLE',
    { prorated: false },
    230,
  ),
  component(
    'OTHER_DEDUCTION',
    'Other deduction',
    'استقطاعات أخرى',
    'DEDUCTION',
    'VARIABLE',
    { prorated: false },
    240,
  ),
]

/** Masks all but the last 4 characters ("•••• 1234"), for identity and account numbers. */
export function maskTail(value: string | null): string | null {
  if (!value) return value
  return value.length <= 4 ? value : `•••• ${value.slice(-4)}`
}

/** The refusals hr returns (problem+json `code`), which the web puts in words. */
export const HR_ERROR_CODES = [
  /** 409: another current employee has this civil number. */
  'EMPLOYEE_CIVIL_NUMBER_TAKEN',
  /** 422: the command does not apply in the employee's status (e.g. reinstating someone not suspended). */
  'EMPLOYEE_STATUS',
  /** 422: a date before the hire date, a last working day too far ahead, a salary dated in a paid month. */
  'EMPLOYEE_DATE_INVALID',
  /** 409: another salary already starts on that date. */
  'SALARY_DATE_TAKEN',
  /** 422: a salary needs the basic salary (load the standard components first). */
  'BASIC_REQUIRED',
  /** 422: an archived component, or a variable one on a salary. */
  'PAY_COMPONENT_NOT_USABLE',
  /** 409: another component has this code. */
  'PAY_COMPONENT_CODE_TAKEN',
  /** 422: the basic salary cannot be archived, recoded or made variable. */
  'PAY_COMPONENT_SYSTEM',
  /** 422: a component on a salary or a run keeps its code and kind. */
  'PAY_COMPONENT_IN_USE',
  /** 422: a cancelled document cannot change. */
  'DOCUMENT_CANCELLED',
  /** 409: another employee already has this system login. */
  'EMPLOYEE_USER_TAKEN',
] as const
export type HrErrorCode = (typeof HR_ERROR_CODES)[number]
